import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reviews } from '@/mocks/manga';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('input[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const res = await fetch('https://readdy.ai/api/form/d9otshaqs38sj6jc7fs0', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await res.text();
      let serverMsg = '';
      try {
        const parsed = JSON.parse(responseText);
        serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';
      } catch {
        serverMsg = responseText;
      }

      if (res.ok && !serverMsg.includes('spam')) {
        const parsed = JSON.parse(responseText);
        if (parsed?.code === 'OK') {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后再试');
        }
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmanzhijia.com/' },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
      {
        '@type': 'Organization',
        name: '韩漫之家',
        url: 'https://www.hanmanzhijia.com',
        description: '韩漫免费漫画在线阅读平台，精选好看韩国漫画，支持下拉式阅读模式。',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@hanmanzhijia.com',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb />

        <main className="pb-14">
          <section className="pt-8 pb-14">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                关于韩漫之家
              </h1>
              <p className="text-sm text-foreground-600 mb-10 leading-relaxed">
                韩漫之家是专注于韩国漫画免费在线阅读的平台。我们致力于为中文读者提供优质的韩漫阅读体验，
                让每一位漫画爱好者都能在这里找到自己喜欢的作品。
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-book-open-line text-xl text-primary-500"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">海量漫画</h3>
                  <p className="text-xs text-foreground-600">收录超过1000部韩国漫画作品，涵盖恋爱、奇幻、动作等多种类型</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-arrow-down-line text-xl text-accent-500"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">下拉式阅读</h3>
                  <p className="text-xs text-foreground-600">创新的下拉式阅读模式，让漫画阅读像浏览网页一样流畅自然</p>
                </div>
                <div className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-shield-check-line text-xl text-secondary-500"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">完全免费</h3>
                  <p className="text-xs text-foreground-600">所有漫画内容完全免费开放，无需任何付费即可畅享阅读</p>
                </div>
              </div>

              <div className="mb-14">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">品牌故事</h2>
                <div className="bg-background-100 rounded-xl p-6 md:p-8">
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    韩漫之家成立于2025年，由一群热爱韩国漫画的年轻人共同创建。我们发现，虽然韩漫在全球范围内越来越受欢迎，
                    但中文读者获取优质韩漫资源的渠道却非常有限。于是，韩漫之家应运而生。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    我们的团队每天都在精心筛选和更新漫画内容，与多位韩国漫画家和出版方保持紧密合作，
                    确保为读者提供最新、最热门的漫画作品。我们相信，好的漫画不应该被语言和地域所限制。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    下拉式阅读模式是我们引以为豪的特色功能。相比传统的翻页阅读，这种模式让读者可以通过简单的下拉手势
                    连续阅读，完全沉浸在漫画的世界中。无论是在电脑还是手机上，都能获得丝滑流畅的阅读体验。
                  </p>
                </div>
              </div>

              <div className="mb-14">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">读者口碑</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {reviews.map((review, index) => (
                    <div key={index} className="bg-background-100 rounded-xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={review.avatar}
                          alt={review.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: review.rating }).map((_, i) => (
                              <span key={i} className="w-3 h-3 flex items-center justify-center text-accent-500">
                                <i className="ri-star-fill text-[10px]"></i>
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-foreground-600 leading-relaxed">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">联系我们</h2>
                <div className="bg-background-100 rounded-xl p-6 md:p-8">
                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                        <i className="ri-check-line text-2xl text-primary-500"></i>
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">感谢您的留言！</h3>
                      <p className="text-sm text-foreground-600">我们会在第一时间回复您，请耐心等待。</p>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            姓名 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-900 placeholder:text-foreground-400"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            邮箱 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-900 placeholder:text-foreground-400"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          type="text"
                          id="subject"
                          name="subject"
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-900 placeholder:text-foreground-400"
                          placeholder="请输入主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          maxLength={500}
                          rows={5}
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-900 placeholder:text-foreground-400 resize-none"
                          placeholder="请输入您想说的话（最多500字）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                      </div>
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="hp-field"
                      />
                      {formStatus === 'error' && (
                        <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                          {formError || '提交失败，请稍后再试'}
                        </div>
                      )}
                      <button
                        type="submit"
                        className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <span className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-send-plane-line"></i>
                        </span>
                        发送留言
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}